import { useMemo } from 'react';

import { resolveRoute, RouteDestination } from '@/components/navigation/utils/route-resolution';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { useMigrationNavigation } from '@/hooks/use-migration-navigation';

export interface RouteDestinationResult {
  destination: RouteDestination;
  url: string | null;
  path: string;
  routePath?: string;
  params?: {
    org: string;
    agent: string;
  };
  isLoading: boolean;
}

/**
 * Hook that determines the destination and URL for a given route.
 *
 * Uses ROUTE_FEATURE_FLAGS (via useMigrationNavigation) as the single source of truth
 * for determining whether a route should stay in Gaia or redirect to Darkwing.
 */
export const useRouteDestination = (
  routeId: string,
  searchParams?: Record<string, string>
): RouteDestinationResult => {
  const { facilityUrlName, orgUrlName } = useCurrentFacility();
  const { getFeatureEnabledStatuses } = useMigrationNavigation();

  // Get feature enabled statuses from ROUTE_FEATURE_FLAGS
  const featureEnabledStatuses = getFeatureEnabledStatuses();

  // Resolve the route
  const resolution = useMemo(() => {
    return resolveRoute(routeId, featureEnabledStatuses, orgUrlName, facilityUrlName, searchParams);
  }, [routeId, featureEnabledStatuses, orgUrlName, facilityUrlName, searchParams]);

  return {
    ...resolution,
    isLoading: false, // Non-blocking initialization, always ready
  };
};
